import { useState, useEffect, useRef } from 'react';
import { Feather, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { API } from '@/lib/axios';
import ProgressDots from './ProgressDots';
import { LOADING_MESSAGES, ONBOARDING_EVENTS } from '@/constants/onboardingConfig';

export default function Screen4Preview({
  onNext,
  onBack,
  memoryInput,
  intent,
  audience,
  sessionId,
  onPreviewReceived,
  preview: cachedPreview,
  trackEvent,
}) {
  const [loadingState, setLoadingState] = useState(cachedPreview ? 'success' : 'loading');
  const [preview, setPreview] = useState(cachedPreview || null);
  const [msgIndex, setMsgIndex] = useState(0);
  const hasFetchedRef = useRef(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (cachedPreview || hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    intervalRef.current = setInterval(() => {
      setMsgIndex((i) => (i + 1) % LOADING_MESSAGES.length);
    }, 2200);

    axios
      .post(`${API}/onboarding/preview`, {
        session_id: sessionId,
        memory_input: memoryInput,
        intent,
        audience,
      })
      .then((res) => {
        clearInterval(intervalRef.current);
        setPreview(res.data);
        setLoadingState('success');
        onPreviewReceived(res.data);
        trackEvent(ONBOARDING_EVENTS.PREVIEW_GENERATED, { success: true });
      })
      .catch(() => {
        clearInterval(intervalRef.current);
        setLoadingState('error');
        trackEvent(ONBOARDING_EVENTS.PREVIEW_GENERATED, { success: false });
      });

    return () => clearInterval(intervalRef.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const retry = () => {
    hasFetchedRef.current = false;
    setLoadingState('loading');
    setMsgIndex(0);
    // Re-trigger via re-mount trick: reset the ref and call effect manually
    hasFetchedRef.current = true;
    intervalRef.current = setInterval(() => {
      setMsgIndex((i) => (i + 1) % LOADING_MESSAGES.length);
    }, 2200);

    axios
      .post(`${API}/onboarding/preview`, {
        session_id: sessionId,
        memory_input: memoryInput,
        intent,
        audience,
      })
      .then((res) => {
        clearInterval(intervalRef.current);
        setPreview(res.data);
        setLoadingState('success');
        onPreviewReceived(res.data);
      })
      .catch(() => {
        clearInterval(intervalRef.current);
        setLoadingState('error');
      });
  };

  return (
    <div className="story-page flex flex-col fade-in">
      <div className="flex-1 flex flex-col px-6 pt-8 pb-4 max-w-md mx-auto w-full">
        <ProgressDots current={3} />

        {/* Loading */}
        {loadingState === 'loading' && (
          <div className="flex-1 flex flex-col items-center justify-center text-center">
            <Feather className="w-10 h-10 text-[var(--story-primary)] mb-6 animate-bounce" />
            <p
              key={msgIndex}
              className="story-body text-base italic transition-opacity duration-500"
            >
              {LOADING_MESSAGES[msgIndex]}
            </p>
          </div>
        )}

        {/* Error */}
        {loadingState === 'error' && (
          <div className="flex-1 flex flex-col items-center justify-center text-center">
            <p className="story-title text-xl mb-3">
              Something slipped away.
            </p>
            <p className="story-meta mb-6">
              Your memory is safe. Want to try again?
            </p>
            <p className="text-xs text-[#A09890] dark:text-[#5C5650] mb-8">
              Your memory is saved. We'll help shape it in the next step.
            </p>
            <div className="flex flex-col gap-3 w-full">
              <Button
                onClick={retry}
                className="story-button-primary w-full"
              >
                Try again
              </Button>
              <Button
                onClick={onNext}
                variant="ghost"
                className="w-full text-[#8C847A] dark:text-[#7C746A] rounded-full py-5"
              >
                Continue without preview
              </Button>
            </div>
          </div>
        )}

        {/* Success */}
        {loadingState === 'success' && preview && (
          <div className="flex-1 flex flex-col">
            <p className="story-label mb-6 text-center">
              Here's how your story begins
            </p>

            {/* Preview card */}
            <div className="story-surface p-5 mb-6">
              {preview.reflection && (
                <p className="font-serif text-sm italic text-[#6B6560] dark:text-[#9C9590] leading-relaxed mb-5">
                  {preview.reflection}
                </p>
              )}

              {preview.title && (
                <div className="flex items-center gap-3 mb-5">
                  <div className="flex-1 h-px bg-[#E0D8CC] dark:bg-[#2A2520]" />
                  <span className="font-serif text-sm font-semibold text-[#2A2420] dark:text-[#F0EBE4] text-center">
                    {preview.title}
                  </span>
                  <div className="flex-1 h-px bg-[#E0D8CC] dark:bg-[#2A2520]" />
                </div>
              )}

              {preview.preview && (
                <p className="font-serif text-base italic text-[#2A2420] dark:text-[#F0EBE4] leading-relaxed">
                  {preview.preview}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-3 mt-auto">
              <Button
                onClick={onNext}
                className="story-button-primary w-full"
              >
                Save this story →
              </Button>
              <button
                onClick={onBack}
                className="flex items-center justify-center gap-1.5 text-sm text-[#A09890] dark:text-[#5C5650] hover:text-[#5C4D6B] dark:hover:text-[#B0A0C0] transition-colors py-2"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Start over
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
